/**
 * 🟦 V11.0g — Captura de screenshot del nodo seleccionado en el iframe.
 *
 * Usa html2canvas dentro del contexto del iframe (mismo origin) para
 * renderizar SOLO el nodo `[data-uib-id="X"]` a un canvas y devolver
 * un PNG base64 listo para enviar al backend / Claude vision.
 *
 * Garantías:
 *   - No remount del iframe (html2canvas pinta a un canvas off-screen)
 *   - Cap defensivo de tamaño (3000x3000px → resize si excede)
 *   - Devuelve null si el nodo no existe o si la captura falla
 *   - No bloquea si tarda >5s (timeout duro)
 */

const MAX_DIM = 1600; // px — el nodo se escala a max 1600 de lado largo.
const TIMEOUT_MS = 5000;

export async function captureNodeScreenshot(iframeEl, uibId) {
  if (!iframeEl || !uibId) return null;
  let win;
  try {
    win = iframeEl.contentWindow;
  } catch {
    return null;
  }
  if (!win) return null;

  // Cargar html2canvas en el iframe si no está. Lo hacemos en el parent y
  // pasamos el target con html2canvas del parent — pero requiere acceso
  // same-origin al document del iframe.
  let doc;
  try {
    doc = iframeEl.contentDocument;
  } catch {
    return null;
  }
  if (!doc) return null;
  const target = doc.querySelector(`[data-uib-id="${CSS.escape(uibId)}"]`);
  if (!target) return null;

  // Import dinámico de html2canvas (chunk separado). Si falla, devolvemos null.
  let html2canvas;
  try {
    const mod = await import(/* webpackChunkName: "html2canvas" */ 'html2canvas');
    html2canvas = mod.default || mod;
  } catch {
    return null;
  }

  // Wrap con timeout duro para no bloquear la UI si html2canvas se cuelga.
  const work = (async () => {
    const rect = target.getBoundingClientRect();
    const scale = Math.min(1, MAX_DIM / Math.max(rect.width || 1, rect.height || 1));
    const canvas = await html2canvas(target, {
      backgroundColor: null,
      scale,
      logging: false,
      useCORS: true,
      allowTaint: false,
      width: rect.width,
      height: rect.height,
    });
    return canvas.toDataURL('image/png');
  })();

  return Promise.race([
    work,
    new Promise((resolve) => setTimeout(() => resolve(null), TIMEOUT_MS)),
  ]).catch(() => null);
}
